import {
  BasicItem,
  type FormData as ItemFormData,
  ReportingItem,
} from "vmd-types";

import { STRAPI_URL } from "../env";

/**
 * Updates only the BasicItem (summary block: name, description, etc).
 * Does NOT handle formData or other ReportingItem properties.
 */
export async function updateBasicItem(
  id: number | string | undefined,
  basicItem: BasicItem
): Promise<BasicItem | undefined> {
  try {
    const response = await fetch(`${STRAPI_URL}/basic-item/${String(id)}`, {
      body: JSON.stringify({ basicItem }),
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      method: "PUT",
    });

    if (!response.ok) {
      throw new Error(
        `update basic-item failed with status ${response.status}.`
      );
    }
    return (await response.json()) as BasicItem;
  } catch {
    return undefined;
  }
}

// `reportingItem` and `formData` ride along so the whole dialog persists in one
// transaction.
export async function createBasicItem(
  basicItem: BasicItem,
  projectId: string | undefined,
  distributionChannel?: string,
  reportingItem?: Partial<ReportingItem>,
  formData?: ItemFormData
): Promise<{ reportingItemId: string; basicItemId: string } | undefined> {
  if (!projectId) return undefined;
  try {
    const response = await fetch(`${STRAPI_URL}/basic-item`, {
      body: JSON.stringify({
        basicItem,
        projectId,
        distributionChannel,
        reportingItem,
        formData,
      }),
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
    });

    if (!response.ok) {
      throw new Error(`save basic-item failed with status ${response.status}.`);
    }
    const created = (await response.json()) as ReportingItem;
    return {
      reportingItemId: String(created.id),
      basicItemId: String(created.basicItem?.id ?? ""),
    };
  } catch {
    return undefined;
  }
}

export async function deleteBasicItem(
  id: number | string | undefined
): Promise<void> {
  try {
    const response = await fetch(`${STRAPI_URL}/basic-item/${String(id)}`, {
      credentials: "include",
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error(
        `delete basic-item failed with status ${response.status}.`
      );
    }
  } catch {
    return;
  }
}
